/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import config from '../../../../share/config';
import session from '../../../../share/session';
import error from '../../../../common/nodejs/errors';
import utils from "../../../../share/util/utils";
import cstmTable from '../../../../share/components/vue/PaginationTable';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import api from '@/common/api';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  WARNING: 'warning',
  DATA_WRITE: 'dataWrite',
  SUCCESS: 'success',
  HANDOVER_ERROR: 'handoverError',
  PREREQUISITES_ERROR: 'prerequisitesError',
  FAILED: 'failed'
};

export default {
  name: 'requirement136',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      requirementId: 135,
      systemId: '',
      buttonDisabled: true,
      /** データリスト */
      dataList: [],
      /** 選択中のコード */
      selectedData: {},
      modalSize: "xl",
      modal_requirement_136: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  components: {
    'cstm-table': cstmTable,
  },

  computed: {
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.HANDOVER_ERROR:
        case PHASE.PREREQUISITES_ERROR:
        case PHASE.FAILED:
          return 'danger';
        case PHASE.WARNING:
          return 'warning';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        case PHASE.DATA_WRITE:
        default:
          return 'secondary';
      }
    },

    ...mapState({
      language: state => state.systems.language,
      netAppId: state => state.netAppConnector.pairingAppId,
      vehicleInfo: state => state.cache.vehicleInfo
    }),

    systemTableColumns() {
      return [
        { name: 'registered_at', label: this.i18nWsKey + '.label_table_registration_date' },
        { name: 'vin', label: 'message.header_measured_data_list_vin' },
        { name: 'registered_by', label: this.i18nWsKey + '.label_filter_registrants' },
        { name: 'data_id', label: this.i18nWsKey + '.label_filter_data_id' }
      ];
    },
  },

  methods: {
    /** 初期化処理 */
    init() {
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.modal_requirement_136 = true;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_136 = false;
    },

    ...mapActions('auth', ['logout', 'resetSession']),

    /**
    * API 通信で共通バリデーションでエラーが発生した際のコールバック
    * @param {object} result バリデーション結果
    */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** その他のエラー */
    showUnexpectedError() {
      this.modalSize = "md";
      // 異常終了：汎用エラーを表示
      this.phase = PHASE.FAILED;
    },

    /**
     * Exit処理
     * @param {*} existence 0:エラー遷移あり, 1:エラー遷移なし 
     * @returns 
     */
    async exit(existence) {
      try {
        this.isLoading = true;
        //バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (existence === 0) {
          // エラー遷移あり
          if (response) {
            // 正常コールバック
            switch (response.data.status) {
              case config.STATUS_WSP_COMPLETED:
                // 正常終了：機能終了
                this.closeWizard();
                break;
              case config.STATUS_WSP_FAILED:
              default:
                // 異常終了：汎用エラー
                this.showUnexpectedError();
                return;
            }
          }
        } else {
          // エラー遷移なし：機能終了
          this.closeWizard();
          return;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * テーブル行押下 
     */
    clickRow: function (selected) {
      this.selectedData = selected;
      this.setHighLight();
      this.buttonDisabled = false;
    },

    /**
     * 選択している機能のハイライト設定
     */
    setHighLight() {
      this.dataList.forEach(item => {
        if (item === this.selectedData) {
          item.fieldSpec = {};
          item.fieldSpec.registered_at = config.LIST_ITEM_HIGHLIGHT;
          item.fieldSpec.vin = config.LIST_ITEM_HIGHLIGHT;
          item.fieldSpec.registered_by = config.LIST_ITEM_HIGHLIGHT;
          item.fieldSpec.data_id = config.LIST_ITEM_HIGHLIGHT;
          item.fieldSpec.locale_key = config.LIST_ITEM_HIGHLIGHT;
        } else {
          if (item.fieldSpec) {
            delete item.fieldSpec;
            // OKボタン押下不可
            this.buttonDisabled = true;
          }
        }
      });
    },

    /** テーブル情報更新 */
    async updateTable() {
      try {
        this.isLoading = true;
        //バックエンドAPI GETのみ
        const response = await individualDiagnose.syncApi(
          this,
          // GET API 呼び出しのコールバック
          async () => {
            return await api.getParamCall(
              config.WSP,
              '/readData/requirements/' + this.requirementId,
              {
                vin: this.vehicleInfo.vehicle.vin.vin,
                system_name: this.systemId,
                work_support_id: this.workSupportId
              }
            );
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // GET API レスポンスチェックのコールバック
          (getApiResponse) => { getApiResponse; return ''; },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // 正常終了：一覧データ取得
          this.dataList = response.data.read_data_list;
          // registered_at(UNIXTIME)をYYYY/MM/DD HH:mm:ss形式に変換
          this.dataList.forEach(item => {
            item.registered_at = utils.timeConverter(item.data_id);
          });
          this.selectedData = {};
          this.setHighLight();
        }

      } finally {
        this.isLoading = false;
      }
    },

    /** 警告表示画面へ遷移 */
    async warningTransition() {
      this.modalSize = "md";
      this.phase = PHASE.WARNING;
    },

    /** サポート読出し */
    async readSupport() {
      try {
        this.isLoading = true;
        //バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'read',
            requirement_id: this.requirementId,
            data_id: this.selectedData.data_id
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // 正常コールバック
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了：データ書き込み画面へ遷移
              this.phase = PHASE.DATA_WRITE;
              // データ書き込みを実行
              await this.writeData();
              break;
            case config.STATUS_WSP_FAILED:
              // 異常終了
              switch (response.data.async_code) {
                case error.WSP_PREREQUISITES_ERROR:
                  // 前提条件エラー表示画面へ遷移
                  this.phase = PHASE.PREREQUISITES_ERROR;
                  break;
                default:
                  // 引継ぎエラー表示画面へ遷移
                  this.phase = PHASE.HANDOVER_ERROR;
                  break;
              }
              break;
            default:
              // 前提条件エラー表示画面へ遷移
              this.phase = PHASE.PREREQUISITES_ERROR;
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** データ書き込み */
    async writeData() {
      try {
        this.isLoading = true;
        //バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'write',
            data_id: this.selectedData.data_id,
            requirement_id: this.requirementId
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // 正常コールバック
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了：マップ生成開始／マップ生成状態確認／マップ生成停止を実行
              await this.mapGeneration();
              break;
            case config.STATUS_WSP_FAILED:
            default:
              // 異常終了：引継ぎエラー表示画面へ遷移
              this.phase = PHASE.HANDOVER_ERROR;
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** マップ生成開始／マップ生成状態確認／マップ生成停止 */
    async mapGeneration() {
      try {
        this.isLoading = true;
        //バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // 正常コールバック
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 正常終了：完了表示画面へ遷移
              this.phase = PHASE.SUCCESS;
              break;
            case config.STATUS_WSP_FAILED:
            default:
              // 異常終了：引継ぎエラー表示画面へ遷移
              this.phase = PHASE.HANDOVER_ERROR;
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },
  },

  async mounted() {
    this.init();
    this.updateTable();
  },
};